import React from 'react';
import { Sparkles } from 'lucide-react';
import { fmtInt, fmtSigned, fmtPct, fmtRate, fmtDay, plural } from '../../lib/slotStatsView';
import { Delta } from './ChartKit';
import { cn } from '@/lib/utils';

/*
 * Round 14b: the figures - headline tiles (value, change over the range, per day, the period
 * before, sparkline), the actions-done ratio tiles and the followers projection (an estimate,
 * labelled as one). Figures use the body sans at semibold with proportional digits (a display face
 * or tabular digits on a big standalone number read as decoration).
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** A trend line without axes: the range in the de-emphasis grey, the latest stretch in the accent. */
export function Sparkline({ values, width = 116, height = 38, testid }) {
  const pts = values.filter(isNum);
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const pad = 4;
  const xy = pts.map((v, i) => [
    pad + (i / (pts.length - 1)) * (width - pad * 2),
    pad + (1 - (v - min) / span) * (height - pad * 2),
  ]);
  const path = (list) => list.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('');
  const tail = xy.slice(Math.max(0, xy.length - Math.max(2, Math.ceil(xy.length / 5))));
  const [ex, ey] = xy[xy.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden className="shrink-0 overflow-visible" data-testid={testid}>
      <path d={path(xy)} fill="none" stroke="var(--viz-deemph)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <path d={path(tail)} fill="none" stroke="var(--viz-line)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={ex} cy={ey} r={3.5} fill="var(--viz-line)" stroke="var(--viz-surface)" strokeWidth={2} />
    </svg>
  );
}

/** "30 days" when every day of the range is recorded, else "10 recorded days". */
export const spanText = (recorded, range) => (recorded === range ? plural(range, 'day') : plural(recorded, 'recorded day'));

/** One headline tile. `h` is headline(). */
export function HeadlineTile({ icon: Icon, label, h, testid }) {
  const valueText = h.text !== null && h.text !== undefined ? h.text : (isNum(h.value) ? fmtInt(h.value) : null);
  const pct = fmtPct(h.pct);
  const prev = h.previous;
  return (
    <div className="aurora-card flex min-w-0 flex-col gap-2 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        <span>{label}</span>
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="font-sans text-[1.9rem] font-semibold leading-none tracking-tight text-foreground" data-testid={testid && `${testid}-value`}>
            {valueText === null ? <span className="text-muted-foreground">-</span> : valueText}
          </p>
          {h.approximate && h.source === 'worker' && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid={testid && `${testid}-approx`}>as Instagram rounds it</p>
          )}
        </div>
        <Sparkline values={h.spark} testid={testid && `${testid}-spark`} />
      </div>
      {h.change === null ? (
        <p className="text-xs text-muted-foreground" data-testid={testid && `${testid}-change`}>No daily change recorded in this range</p>
      ) : (
        <div className="space-y-0.5">
          <p className="flex flex-wrap items-center gap-x-1.5 text-sm font-semibold text-foreground tnum" data-testid={testid && `${testid}-change`}>
            <Delta value={h.change} />
            {pct && <span className="font-medium text-muted-foreground">({pct})</span>}
            <span className="font-normal text-muted-foreground">in {spanText(h.recordedDays, h.range)}</span>
          </p>
          <p className="text-xs text-muted-foreground tnum" data-testid={testid && `${testid}-perday`}>
            {fmtRate(h.perDay)} a day on average
          </p>
          {prev && (
            <p className="text-xs text-muted-foreground tnum" data-testid={testid && `${testid}-previous`}>
              Previous {plural(prev.days, 'day')}: <span className="text-foreground/90">{fmtSigned(prev.change)}</span>
              {prev.recordedDays < prev.days ? ` over ${plural(prev.recordedDays, 'recorded day')}` : ''} ({fmtRate(prev.perDay)} a day)
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const RATIO_TEXT = {
  perFollow: {
    title: 'Followers per 100 follows',
    noun: 'follows',
    def: 'Followers gained ÷ follows done × 100, on the same dates.',
  },
  perAction: {
    title: 'Followers per 100 actions',
    noun: 'actions',
    def: 'Followers gained ÷ (likes + comments + follows + welcome DMs + story likes) × 100, on the same dates.',
  },
};

/**
 * The actions-done ratios over the selected range as stat tiles: the value, what it was computed
 * from (gained ÷ done, over how many days that have both), and the definition. A '-' says why.
 * `noOverlapWhy`: the dates that explain a range with no day holding both.
 */
export function RatioTiles({ ratios, noOverlapWhy }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {ratios.map((r) => {
        const t = RATIO_TEXT[r.key] || { title: r.key, noun: 'actions', def: '' };
        let detail;
        if (r.value !== null) {
          detail = `${fmtSigned(r.gained)} followers ÷ ${fmtInt(r.done)} ${t.noun}, over ${plural(r.days, 'day')} with both recorded`;
        } else if (r.reason === 'noOverlap') {
          detail = `No day in this range has both a follower change and ${t.noun} recorded${noOverlapWhy ? ` (${noOverlapWhy})` : ''}.`;
        } else {
          detail = `No ${t.noun} on the ${plural(r.days, 'day')} of this range that have a follower change, so there is nothing to divide by.`;
        }
        return (
          <div key={r.key} className="rounded-xl border border-white/5 bg-white/[.02] p-4" data-testid={`ratio-${r.key}`}>
            <p className="text-xs font-medium text-muted-foreground">{t.title}</p>
            <p className={cn('mt-2 font-sans text-2xl font-semibold leading-none', r.value === null ? 'text-muted-foreground' : 'text-foreground')}
              data-testid={`ratio-${r.key}-value`}>
              {r.value === null ? '-' : r.value.toFixed(1)}
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground tnum" data-testid={`ratio-${r.key}-detail`}>{detail}</p>
            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{t.def}</p>
          </div>
        );
      })}
    </div>
  );
}

/** The followers projection. Always says it is an estimate and what it assumes. */
export function ProjectionBlock({ p, range }) {
  if (!p.ok) {
    let text;
    if (p.reason === 'noHistory') text = 'No follower history yet, so there is nothing to project from.';
    else if (p.reason === 'stale') {
      text = `No projection: the latest profile check is ${plural(p.age, 'day')} old (${fmtDay(p.day0, 'medium')}). `
        + 'A projection from it would end in the past.';
    } else text = `Not enough history to project from: ${p.have} of the ${p.need} recorded days needed.`;
    return <p className="text-sm text-muted-foreground text-pretty" data-testid="projection-unavailable">{text}</p>;
  }
  return (
    <div data-testid="projection">
      <p className="text-sm text-muted-foreground">
        At the pace of the last {plural(p.windowDays, 'recorded day')}
        {' '}(<span className="font-semibold text-foreground tnum">{fmtRate(p.perDay)}</span> followers a day), from the
        {' '}{fmtDay(p.day0, 'medium')} check:
      </p>
      <table className="mt-3 w-full text-sm tnum">
        <caption className="sr-only">Estimated followers after 7, 30 and 90 more days</caption>
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="py-1.5 pr-3 font-medium">In</th>
            <th scope="col" className="py-1.5 pr-3 text-right font-medium">Est. change</th>
            {p.base !== null && <th scope="col" className="py-1.5 pr-3 text-right font-medium">Est. followers</th>}
            <th scope="col" className="py-1.5 text-right font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {p.horizons.map((hz) => (
            <tr key={hz.days} className="border-t border-white/5" data-testid={`projection-row-${hz.days}`}>
              <th scope="row" className="whitespace-nowrap py-1.5 pr-3 text-left font-normal text-muted-foreground">{hz.days} days</th>
              <td className="py-1.5 pr-3 text-right font-semibold text-foreground">
                <Delta value={hz.change} className="justify-end" />
              </td>
              {p.base !== null && <td className="py-1.5 pr-3 text-right text-foreground">{fmtInt(hz.count)}</td>}
              <td className="whitespace-nowrap py-1.5 text-right text-muted-foreground">{fmtDay(hz.date, 'medium')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 flex gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
        <Sparkles className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        <span>
          An estimate, not a forecast: it assumes the average daily change of the selected {range}-day range simply
          continues. Real growth is rarely a straight line.
        </span>
      </p>
    </div>
  );
}
